// client/src/components/social/ActivityFeed.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, Alert
} from '@mui/material';
import ActivityItem from './ActivityItem';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * Paginated activity feed.
 *
 * Props:
 *   userId        — if provided, shows that user's public activity; omit to show the follower feed
 *   currentUserId — the logged-in user's id
 *   mutualFollowSet — Set of userIds that have a mutual follow with currentUser (optional, for profile view)
 */
export default function ActivityFeed({ userId, currentUserId, mutualFollowSet }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [activities, setActivities] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);

  const isEnabled = isFeatureEnabled('enableActivityFeed');

  const fetchActivities = useCallback(async (cursor = null) => {
    setLoading(true);
    setError(null);
    try {
      const url = userId ? `/activity/user/${userId}` : '/activity/feed';
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      const res = await axiosInstance.get(url, { params });
      if (cursor) {
        setActivities(prev => [...prev, ...res.data.activities]);
      } else {
        setActivities(res.data.activities);
      }
      setNextCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
      setIsPrivate(res.data.isPrivate || false);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
      setError('Failed to load activity feed.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!isEnabled) return;
    fetchActivities(null);
  }, [isEnabled, fetchActivities]);

  const loadMore = () => {
    if (nextCursor) fetchActivities(nextCursor);
  };

  if (!isEnabled) return null;

  if (isPrivate) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">This user's activity is private.</Typography>
      </Box>
    );
  }

  if (loading && activities.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (activities.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {userId ? 'No activity yet.' : 'Follow people to see their activity here.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {activities.map(a => (
        <ActivityItem
          key={a._id}
          activity={a}
          currentUserId={currentUserId}
          isMutual={mutualFollowSet ? mutualFollowSet.has(String(a.userId?._id)) : false}
        />
      ))}
      {hasMore && (
        <Button onClick={loadMore} disabled={loading} variant="outlined" fullWidth sx={{ mt: 2 }}>
          {loading ? 'Loading...' : 'Load more'}
        </Button>
      )}
    </Box>
  );
}
